<template>
  <DxPopup v-bind="popupConfig" @shown="onShown" @hidden="onHidden">
    <DxToolbarItem
      :options="searchOptions"
      widget="dxButton"
      location="after"
    />
    <GcPopUpSearchForItem
      v-if="searchForItem && searchForItem.visible"
      :setVisible="searchForItem.visible"
      :callBack="searchForItem.popUpCallBack"
    />
    <template #content>
      <div>
        <div class="css-v2 gc-popup-form">
          <gc-form
            :data-model="formModel"
            :formItems="getFormItems"
            :formOptions="{ gcLabelLocation: 'left' }"
            :formMode="'edit'"
            :isNormal="true"
            ref="updateItemForm"
          />
        </div>

        <div class="update-item-popup-footer">
          <DxButton
            text="Cancel"
            type="default"
            styling-mode="contained"
            @click="attemptClose"
          />
          <DxButton
            text="Save"
            type="default"
            styling-mode="contained"
            class="save-btn"
            :disabled="!itemHasChanged() || pricingInProgress"
            @click="attemptSave"
            ref="footer-save-btn"
          />
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import ApiService from "@/_services/api/api.service";
import { ProductService } from "@/_services/api/products/product.service";
import { ProductUnitOfMeasureService } from "@/_services/api/products/product-unit-of-measure.service";
import { SalesOrderV3Service } from "@/_services/api/sales/sales-order-v3.service";
import GcPopUpSearchForItem from "@/popups/gc-popup-search-for-item";
import SalesFormulas from "@/_helpers/formulas/sales-formulas";
import { SalesOrderState } from "@/_models/sales-order";

// Update Item (2113/76040) — revised 2026-08-28 (technical-design.md §0) to
// lift-and-shift the despatch Order "Edit Item" popup's full-reprice
// behaviour: selecting a New Item or changing UM triggers a full
// setSaleItemPrice reprice with NO field-freezing. Price, Discount %, Tax
// Code, WET%, costs, UM conversion and all line totals all follow the New
// Item's own pricing (BR-6/BR-7a/BR-8a). The previous PROTECTED_PRICE_FIELDS
// snapshot/restore mechanism (which caused Bug 76123 by falling out of sync
// with setSaleItemPrice) has been deleted outright — that deletion is itself
// the fix.

export default {
  name: "gc-popup-sales-order-update-item",

  components: { GcPopUpSearchForItem },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // Line's current ItemCode (the Dummy Item code) — read-only display only.
    currentItemCode: {
      type: String,
      default: "",
    },
    // Line's current Description — read-only display only.
    currentDescription: {
      type: String,
      default: "",
    },
    // Line's existing comment — pre-fills the popup's Line Comment field.
    lineComment: {
      type: String,
      default: "",
    },
    // Passed straight through into the "saved" payload so the caller can
    // identify which grid row to update.
    rowKey: {
      type: [String, Number],
      default: null,
    },
    // A snapshot of the line's full current row data — read only, to seed
    // the local repricing working copy built in repriceWorkingRecord. Never
    // mutated directly; nothing on the live grid row changes until Save.
    rowData: {
      type: Object,
      default: () => ({}),
    },
    // Header context setSaleItemPrice needs to price/cost the New Product —
    // mirrors the arguments every other repricing call site on
    // sales-order.vue already passes.
    salesOrderTypeCode: { type: String, default: "" },
    branchCode: { type: String, default: "" },
    customerCode: { type: String, default: "" },
    exchangeRate: { type: Number, default: 1 },
    dateIn: { type: [String, Date], default: null },
    requiredByDate: { type: [String, Date], default: null },
  },

  emits: ["update:visible", "saved"],

  data() {
    return {
      newProductCode: null,
      newProductDescription: "",
      // The UM field's current value — defaults to the New Product's own UM
      // when a New Item is selected (the old line's SalesUM may not exist on
      // the new product at all), but is user-editable via the UM dxSelectBox
      // and re-triggers a full reprice on change.
      newProductUM: null,
      // Quantity/Price/Discount % — pre-filled from the Dummy Item line's
      // own current values at popup open and freely editable from the
      // start, independent of whether a New Item is ever picked (BR-14,
      // T2.3 — Save must be reachable via a Qty/Price/Discount %/UM/
      // Comment-only change).
      newItemQty: null,
      newItemPrice: null,
      newItemDiscPcnt: null,
      // Snapshot of Item/UM/Qty/Price/Discount/Comment at popup open —
      // itemHasChanged() compares the live form against this (BR-14).
      originalSnapshot: null,
      comment: "",
      dirty: false,
      // True while setSaleItemPrice is in flight for the current selection —
      // Save is disabled meanwhile so a stale/partial price can't be saved.
      pricingInProgress: false,
      // The fully repriced/recosted local working copy for the current
      // selection (Price/Discount %/Tax Code/WET% all follow the New Item,
      // per BR-6/BR-7a/BR-8a) — built by repriceWorkingRecord, emitted as-is
      // by attemptSave.
      pricedRecord: null,
      // Guards against a stale setSaleItemPrice response being applied after
      // the user has already changed the selection again.
      selectionToken: 0,
      // Drives the SEARCH ITEM browse popup (gc-popup-search-for-item) —
      // undefined/hidden until the toolbar button is clicked.
      searchForItem: undefined,
      searchOptions: {
        text: "SEARCH ITEM",
        type: "default",
        stylingMode: "contained",
        onClick: this.searchForNewItem,
      },
      // The last item code that passed api/LineItemData validation this
      // popup session — what an invalid typed code reverts back to.
      lastValidItemCode: null,
    };
  },

  computed: {
    popupConfig() {
      return {
        visible: this.visible,
        title: "Update Item",
        width: "65%",
        height: 340,
        dragEnabled: false,
        showCloseButton: false,
        hideOnOutsideClick: false,
        wrapperAttr: { class: "css-v2 gc-popup" },
        showTitle: true,
      };
    },

    formModel() {
      return {
        CurrentItemCode: this.currentItemCode,
        CurrentDescription: this.currentDescription,
        NewProductCode: this.newProductCode,
        NewProductDescription: this.newProductDescription,
        UM: this.newProductUM,
        Quantity: this.newItemQty,
        Price: this.newItemPrice,
        Discount: this.newItemDiscPcnt,
        IncTotal: this.pricedRecord ? this.pricedRecord.FXLineTotal : null,
        Comment: this.comment,
      };
    },

    // Sourced from the New Item + the order's branch — no Inactive filter,
    // matching both technical-design.md §5 and the despatch precedent
    // (gc-popup-edit-product-item.vue's getSalesUMDataSourceConfig) exactly.
    // Falls back to the current (Dummy Item) code until a New Item is
    // picked, since UM is independently editable from popup open (BR-14).
    newItemUMDataSource() {
      return ProductUnitOfMeasureService.dataSourceConfig({
        filter: [
          ["ProductCode", "=", this.newProductCode || this.currentItemCode],
          ["BranchCode", "=", this.branchCode],
        ],
      });
    },

    // Two-column, 10-field layout — mirrors gc-popup-edit-product-item.vue's
    // getFormItems exactly (left column: Current Item, New Item, UM,
    // Quantity, Price, Discount %, Inc Total; right column: Current Item
    // Description, New Item Description, Comment), per technical-design.md
    // §6's Fields table.
    getFormItems() {
      return [
        {
          itemType: "group",
          colCount: 2,
          items: [
            {
              itemType: "group",
              items: [
                {
                  dataField: "CurrentItemCode",
                  label: { text: "Current Item" },
                  editorOptions: { allowUpdate: false },
                },
                {
                  dataField: "NewProductCode",
                  label: { text: "New Item" },
                  editorType: "dxTextBox",
                  required: true,
                  editorOptions: {
                    allowUpdate: true,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: (e) => {
                      this.onNewItemCodeChanged(e);
                    },
                    onKeyDown: (e) => {
                      this.goToNextField(e, "UM");
                    },
                  },
                },
                {
                  dataField: "UM",
                  label: { text: "UM" },
                  editorType: "dxSelectBox",
                  editorOptions: {
                    dataSource: this.newItemUMDataSource,
                    valueExpr: "UnitOfMeasureCode",
                    // No description field exists on the ProductUnitOfMeasure
                    // entity — matches the despatch precedent's
                    // getSalesUMExpr, which shows only the raw UM code too.
                    displayExpr: "UnitOfMeasureCode",
                    onValueChanged: (e) => {
                      this.onUMChanged(e);
                    },
                    onKeyDown: (e) => {
                      this.goToNextField(e, "Quantity");
                    },
                  },
                },
                {
                  dataField: "Quantity",
                  label: { text: "Quantity" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: (e) => {
                      this.onQuantityChanged(e);
                    },
                    onKeyDown: (e) => {
                      this.goToNextField(e, "Price");
                    },
                  },
                },
                {
                  dataField: "Price",
                  label: { text: "Price" },
                  editorType: "dxTextBox",
                  format: "Decimal-16-2",
                  editorOptions: {
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: (e) => {
                      this.onPriceChanged(e);
                    },
                    onKeyDown: (e) => {
                      this.goToNextField(e, "Discount");
                    },
                  },
                },
                {
                  dataField: "Discount",
                  label: { text: "Discount %" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: (e) => {
                      this.onDiscountChanged(e);
                    },
                    onKeyDown: (e) => {
                      this.goToNextField(e, "Comment");
                    },
                  },
                },
                {
                  dataField: "IncTotal",
                  label: { text: "Inc Total" },
                  editorType: "dxTextBox",
                  format: "Decimal-16-2",
                  editorOptions: { allowUpdate: false },
                },
              ],
            },
            {
              itemType: "group",
              items: [
                {
                  dataField: "CurrentDescription",
                  label: { text: "Current Item Description" },
                  editorOptions: { allowUpdate: false },
                },
                {
                  dataField: "NewProductDescription",
                  label: { text: "New Item Description" },
                  editorOptions: { allowUpdate: false },
                },
                {
                  dataField: "Comment",
                  label: { text: "Line Comment" },
                  editorType: "dxTextArea",
                  editorOptions: {
                    height: 140,
                    maxLength: 1000,
                    onValueChanged: (e) => {
                      this.onCommentChanged(e);
                    },
                    onKeyDown: (e) => {
                      if (
                        e.event.key === "Enter" ||
                        (e.event.key === "Tab" && !e.event.shiftKey)
                      ) {
                        setTimeout(() => {
                          this.$refs["footer-save-btn"].instance.focus();
                        }, 1);
                      }
                    },
                  },
                },
              ],
            },
          ],
        },
      ];
    },
  },

  created() {
    document.addEventListener("keydown", this.onKeyDown);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeyDown);
  },

  methods: {
    onShown() {
      this.newProductCode = null;
      this.newProductDescription = "";
      // Quantity/Price/Discount %/UM all pre-fill from the Dummy Item
      // line's own current values, editable immediately — BR-14/T2.3 allow
      // Saving after changing only one of these, with Item never touched.
      this.newProductUM = this.rowData.SalesUM;
      this.newItemQty = this.rowData.SalesUMQty;
      this.newItemPrice = this.rowData.SalesUMFXPrice;
      this.newItemDiscPcnt = this.rowData.DiscPcnt;
      this.comment = this.lineComment;
      this.dirty = false;
      this.pricingInProgress = false;
      this.selectionToken = 0;
      this.lastValidItemCode = null;
      // Seeded from the current line so Quantity/Price/Discount/UM edits
      // have something to mutate locally before any Item/UM reprice has
      // ever run.
      this.pricedRecord = { ...this.rowData };
      this.originalSnapshot = {
        UM: this.newProductUM,
        Qty: this.newItemQty,
        Price: this.newItemPrice,
        DiscPcnt: this.newItemDiscPcnt,
        Comment: this.comment,
      };
    },

    onHidden() {
      this.$emit("update:visible", false);
    },

    markDirty() {
      this.dirty = true;
    },

    onCommentChanged(e) {
      // formModel is a one-way (non-.sync) computed prop passed into
      // gc-form, so typing here only updates gc-form's own internal copy
      // unless we explicitly capture it back onto this.comment — without
      // this, Save always emitted the stale value set in onShown(), which
      // is exactly Bug 76122 (Line Comment edit not saved / reverts on
      // reopen).
      this.comment = e.value;
      if (e.event) this.markDirty();
    },

    // Shared by New Item change and UM change — both trigger the same full
    // setSaleItemPrice reprice, with no protected-field restore (BR-6/
    // BR-7a/BR-8a; this is the fix for Bug 76123). Safe to pass the same
    // object as both newData and currentRowData to setSaleItemPrice —
    // setSaleItemPriceAllLines does the same (sales-order.vue's
    // setSaleItemPriceAllLines, "REPRICE ALL"). On a failed lookup,
    // setSaleItemPrice's own .catch() swallows the error without mutating
    // workingRecord further, so ItemCode/OriginItemCode/Description/SalesUM
    // still apply even if the reprice fails (BR-13, best-effort).
    async repriceWorkingRecord({ itemCode, description, salesUM }) {
      // Base off the last priced state if there is one (a UM-only change
      // reprices from the already-selected New Item, not back to the
      // original Dummy Item row) — read before pricedRecord is reset below.
      var baseRecord = this.pricedRecord || this.rowData;

      // Invalidate any pricing call still in flight for a previous
      // Item/UM change — its response will be discarded when it resolves
      // (see the token check below).
      this.selectionToken++;
      var token = this.selectionToken;
      this.pricingInProgress = true;
      this.pricedRecord = null;

      // Local working copy — the live grid row is never touched here;
      // nothing applies to the actual line until Save.
      var workingRecord = {
        ...baseRecord,
        ItemCode: itemCode,
        // Set before the reprice call — setSaleItemPrice resolves the item
        // to price from OriginItemCode in preference to ItemCode (§4).
        OriginItemCode: itemCode,
        Description: description,
        SalesUM: salesUM,
      };

      try {
        await SalesOrderV3Service.setSaleItemPrice(
          workingRecord,
          workingRecord,
          this.salesOrderTypeCode,
          this.branchCode,
          this.customerCode,
          this.exchangeRate,
          this.dateIn,
          this.requiredByDate ? new Date(this.requiredByDate) : null,
          false,
        );
        // No protected-field restore — setSaleItemPrice's full result
        // (Price, Discount %, Tax Code, WET%, costs, UM conversion, all
        // line totals) is kept as-is.
      } finally {
        if (token === this.selectionToken) {
          this.pricedRecord = workingRecord;
          // Resync the popup's own display fields — setSaleItemPrice always
          // overwrites Price/Discount % from the New Item's own pricing
          // (matching despatch's "picking a new item resets pricing"
          // behaviour), so any manual Price/Discount edit made before this
          // reprice ran is intentionally discarded. Quantity is never
          // touched by setSaleItemPrice itself, so it survives unchanged.
          this.newItemQty = workingRecord.SalesUMQty;
          this.newItemPrice = workingRecord.SalesUMFXPrice;
          this.newItemDiscPcnt = workingRecord.DiscPcnt;
          this.pricingInProgress = false;
        }
        // else: superseded by a newer Item/UM change while this call was in
        // flight — discard, the newer call owns the result.
      }
    },

    // New Item field's onValueChanged — typed code, validated on blur/Enter
    // (DevExtreme dxTextBox's default valueChangeEvent is "change", not
    // every keystroke). formModel is one-way, so the value is always
    // captured back onto this.newProductCode first (mirrors
    // onCommentChanged/onUMChanged); validation only runs for a real user
    // edit (e.event set), not a programmatic set (e.g. our own
    // revert-on-invalid write below).
    onNewItemCodeChanged(e) {
      this.newProductCode = e.value ? e.value.trim() : null;
      if (!e.event) return;
      this.validateNewItemCode(this.newProductCode);
    },

    // Shared entry point for both the typed-code path and the SEARCH ITEM
    // browse result — mirrors despatch's updateFormModel being called from
    // both its onValueChanged handler and its searchForItemCallBack.
    // skipActiveCheck is set by the browse path only — gc-popup-search-for-
    // item's own query already filters Inactive eq 'N' (user-stories.md:
    // "Browse path, already active-only in the precedent"), so re-checking
    // here would be redundant; the typed-code path always needs the check
    // since a typed code hasn't passed through any active-only filter yet.
    async validateNewItemCode(itemCode, { skipActiveCheck = false } = {}) {
      if (!itemCode) {
        // New Item field cleared — Item reverts to the original Dummy
        // Item; Qty/Price/Discount/UM/Comment edits already made are
        // independent (BR-14) and must survive this.
        this.newProductDescription = "";
        this.pricedRecord.ItemCode = this.rowData.ItemCode;
        this.pricedRecord.OriginItemCode =
          this.rowData.OriginItemCode || this.rowData.ItemCode;
        this.pricedRecord.Description = this.rowData.Description;
        return;
      }

      this.pricingInProgress = true;
      var lineItemData = await this.getNewItemLineData(itemCode, skipActiveCheck);

      if (!lineItemData) {
        // Unknown or inactive (Inactive eq 'N' is part of the lookup
        // filter below) — BR-4/§11 OI-1. Revert to the last item that
        // actually passed validation this session (our New Item field
        // starts empty, unlike despatch's pre-filled Code field, so there
        // is no "original" item of our own to revert to).
        await this.$alert(`Product ${itemCode} does not exist.`, "Warning");
        this.newProductCode = this.lastValidItemCode;
        this.pricingInProgress = false;
        this.focusNewItemCodeField();
        return;
      }

      this.lastValidItemCode = itemCode;
      this.newProductDescription = lineItemData[0].Description;
      // Default UM shown in the UM field until the user picks a different
      // one — the old line's SalesUM may not exist on the new product.
      this.newProductUM = lineItemData[0].UnitOfMeasureCode;
      this.markDirty();

      await this.repriceWorkingRecord({
        itemCode,
        description: this.newProductDescription,
        salesUM: this.newProductUM,
      });
    },

    // api/LineItemData lookup — mirrors despatch's getProductDetails().
    // api/LineItemData itself exposes no Inactive column (its backing view,
    // api.LineItemDataView, never selects one from ProductList, even though
    // ProductList carries it) — confirmed by a runtime OData error when a
    // filter referenced it. Active status is checked separately via
    // api/Product below (which does expose Inactive directly), but only for
    // the typed-code path — a browse-selected item already passed through
    // gc-popup-search-for-item's own Inactive eq 'N' query, so
    // validateNewItemCode passes skipActiveCheck=true for that path rather
    // than re-checking a value we already know. Network errors are
    // swallowed, same as despatch — treated as "not found".
    async getNewItemLineData(itemCode, skipActiveCheck) {
      if (!skipActiveCheck) {
        var isActive = await this.checkProductIsActive(itemCode);
        if (!isActive) return null;
      }

      const requestScreenData = {
        url: "api/LineItemData",
        top: "1",
        filter: `Code eq '${itemCode}' and BranchCode eq '${this.branchCode}' and IsDefault eq 'Y'`,
      };
      var lineItemData;
      await ApiService.oDataGet(requestScreenData)
        .then((res) => {
          if (res.data.value.length > 0) lineItemData = res.data.value;
        })
        .catch(() => {});
      return lineItemData;
    },

    async checkProductIsActive(itemCode) {
      var isActive = false;
      await ProductService.getByCode(itemCode, { select: "Code,Inactive" })
        .then((res) => {
          isActive = res.data.value.length > 0 && res.data.value[0].Inactive === "N";
        })
        .catch(() => {});
      return isActive;
    },

    focusNewItemCodeField() {
      setTimeout(() => {
        this.$refs["updateItemForm"].$refs["gc-form-comp"].instance
          .getEditor("NewProductCode")
          .focus();
      }, 10);
    },

    searchForNewItem() {
      // No productListQuery passed — gc-popup-search-for-item falls back to
      // its own active-only ProductList default (Inactive eq 'N'), same as
      // the despatch precedent's "so" path.
      this.searchForItem = {
        visible: true,
        popUpCallBack: this.onSearchForItemResult,
      };
    },

    onSearchForItemResult(item, updated) {
      if (updated) {
        this.newProductCode = item.Code;
        // Already active — see getNewItemLineData's comment.
        this.validateNewItemCode(item.Code, { skipActiveCheck: true });
      }
      this.searchForItem = undefined;
    },

    onUMChanged(e) {
      // Always capture the value back (mirrors onCommentChanged) so
      // gc-form's internal copy and this.newProductUM never diverge.
      this.newProductUM = e.value;
      // e.event is unset for a programmatic value change (e.g. the default
      // UM applied right after a New Item selection, which is already being
      // priced by validateNewItemCode) — only a real user pick should
      // trigger its own reprice.
      if (!e.event) return;
      this.markDirty();
      // UM is independently editable from popup open (BR-14) — reprice
      // against the New Item if one has been picked, otherwise against the
      // current (Dummy Item) code.
      this.repriceWorkingRecord({
        itemCode: this.newProductCode || this.currentItemCode,
        description: this.newProductDescription || this.rowData.Description,
        salesUM: this.newProductUM,
      });
    },

    // Quantity/Price/Discount % — local recalculation only, no server call
    // (BR-8a). Each always captures its value back first (formModel is
    // one-way), then recalculates only for a real user edit.
    onQuantityChanged(e) {
      this.newItemQty = e.value;
      if (!e.event) return;
      this.markDirty();
      this.recalculateLocalTotals();
    },

    onPriceChanged(e) {
      this.newItemPrice = e.value;
      if (!e.event) return;
      this.markDirty();
      this.recalculateLocalTotals();
    },

    onDiscountChanged(e) {
      this.newItemDiscPcnt = e.value;
      if (!e.event) return;
      this.markDirty();
      this.recalculateLocalTotals();
    },

    // Mirrors the grid's own Price/Discount-edit handlers
    // (onPriceUpdateValidateGPToolPrcnt/onDiscountPrctUpdate,
    // sales-order.vue) minus their margin-breach warning dialog — whether
    // this popup should also trigger that screen-wide check is an
    // unresolved GAP the team flagged separately (test-cases.md), not part
    // of this task. SalesOrderState.setStockPrices/setLineTotals
    // (src/_models/sales-order.js) is the same SalesFormulas chain
    // technical-design.md names, already packaged as the convenience
    // wrapper every grid-level handler uses.
    recalculateLocalTotals() {
      var record = this.pricedRecord;
      if (!record) return;

      record.SalesUMQty = this.newItemQty;
      record.SalesUMFXPrice = this.newItemPrice;
      record.DiscPcnt = this.newItemDiscPcnt;

      record.SalesUMNetFXPrice = SalesFormulas.calculateSalesUMNetFXPrice(
        record.SalesUMFXPrice,
        record.DiscPcnt,
      );
      record.SalesUMPrice = SalesFormulas.calculateSalesUMPriceFromSalesUMFXPrice(
        record.SalesUMFXPrice,
        this.exchangeRate,
      );
      record.SalesUMNetPrice =
        SalesFormulas.calculateSalesUMNetPriceFromSalesUMNetFXPrice(
          record.SalesUMNetFXPrice,
          this.exchangeRate,
        );

      SalesOrderState.setStockPrices(record);
      SalesOrderState.setLineTotals(record, { ExchangeRate: this.exchangeRate });
    },

    // BR-14 — replaces the old !newProductCode Save-gate. Save is enabled
    // once any of Item/UM/Quantity/Price/Discount %/Comment differs from
    // the line's values when the popup opened (despatch itemHasChanged()).
    itemHasChanged() {
      if (!this.originalSnapshot) return false;
      if (this.newProductCode) return true;
      if (this.newProductUM !== this.originalSnapshot.UM) return true;
      if (this.newItemQty !== this.originalSnapshot.Qty) return true;
      if (this.newItemPrice !== this.originalSnapshot.Price) return true;
      if (this.newItemDiscPcnt !== this.originalSnapshot.DiscPcnt) return true;
      if (this.comment !== this.originalSnapshot.Comment) return true;
      return false;
    },

    // Ported as-is from gc-popup-edit-product-item.vue's goToNextField —
    // generic, no orderType dependency.
    goToNextField(e, dataField) {
      if (
        e.event.key === "Enter" ||
        (e.event.key === "Tab" && !e.event.shiftKey)
      ) {
        setTimeout(() => {
          this.$refs["updateItemForm"].$refs["gc-form-comp"].instance
            .getEditor(dataField)
            .focus();
        }, 1);
      }
    },

    onKeyDown(e) {
      if (!this.visible) return;

      if (e.key === "Escape") {
        this.attemptClose();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        if (this.itemHasChanged() && !this.pricingInProgress) {
          e.preventDefault();
          this.attemptSave();
        }
      }
    },

    async attemptClose() {
      if (this.dirty) {
        var confirmed = await this.$confirm(
          "You have unsaved changes on this Update Item form. Are you sure you want to discard them?",
          "Discard changes?",
        );
        if (!confirmed) return;
      }
      this.$emit("update:visible", false);
    },

    attemptSave() {
      if (!this.itemHasChanged() || this.pricingInProgress) return;

      // pricedRecord is always populated from onShown() onward (seeded
      // from rowData, then kept current by every reprice/local-recalc) —
      // carries the full line state: ItemCode/OriginItemCode/Description/
      // SalesUM plus Price/Discount %/Tax Code/WET%/costs/line totals.
      this.$emit("saved", {
        ...this.pricedRecord,
        rowKey: this.rowKey,
        Comment: this.comment,
      });
      this.dirty = false;
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
.update-item-popup-footer {
  padding: 0px 10px 10px 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
